/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import i18n from '@shared/i18n';


interface ChunkRetryOptions {
  maxRetries?: number;
  retryDelay?: number;
  onRetry?: (retryCount: number) => void;
}

let _updateAvailable = false;

/**
 * Marks that a newer app version was detected so chunk-load failures reload
 * immediately instead of clearing cache first.
 */
export function markUpdateAvailable(): void {
  _updateAvailable = true;
}

/**
 * @returns `true` when a newer app version has been flagged as available.
 */
export function isUpdateAvailable(): boolean {
  return _updateAvailable;
}

/**
 * Classifies whether a dynamic-import error is a transient chunk/network
 * failure worth retrying (versus a genuine application error).
 *
 * @param error The error thrown by the import.
 * @returns `true` when the failure is retryable.
 */
const isRetryableError = (error: Error): boolean => {
  const retryableErrors = ['ChunkLoadError', 'TypeError'];
  const retryableMessages = [
    'Loading chunk',
    'Loading CSS chunk',
    'Failed to fetch',
    'Network Error',
    'ERR_NETWORK',
    'fetch',
    'failed',
    'timeout',
    'CORS',
    'chunk'
  ];
  return retryableErrors.includes(error.name) ||
         retryableMessages.some(pattern => error.message.includes(pattern));
};

/**
 * Best-effort purge of all Cache Storage entries so a stale chunk is not served
 * again after reload. Swallows errors (cache clearing is non-critical).
 */
const clearBrowserCache = async (): Promise<void> => {
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
    } catch (cacheError) {
      console.warn('Erro ao limpar cache:', cacheError);
    }
  }
};

/**
 * Shows a transient, self-styled toast telling the user the app is updating,
 * right before an automatic reload. Uses an inline DOM node so it works even if
 * React failed to load a chunk.
 */
function showUpdateToast(): void {
  const toast = document.createElement('div');
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  Object.assign(toast.style, {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#1a1a2e',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'system-ui, sans-serif',
    zIndex: '999999',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    opacity: '0',
    transition: 'opacity 0.2s ease',
  });
  toast.textContent = i18n.t('loading.updating', { ns: 'common' });
  document.body.appendChild(toast);
  requestAnimationFrame(() => { toast.style.opacity = '1'; });
}

/**
 * Reloads the page after a delay, giving the update toast time to render.
 *
 * @param delay Milliseconds to wait before reloading.
 */
const reloadPageAfterDelay = (delay: number): void => {
  setTimeout(() => {
    console.log('Recarregando página devido a erro de carregamento...');
    window.location.reload();
  }, delay);
};

/**
 * Recovers from a retryable chunk-load error by notifying the user and
 * reloading. When an update is known, reloads fast; otherwise clears the cache
 * first. Returns a never-resolving promise so callers halt while the reload
 * takes over.
 *
 * @param error The retryable error that occurred.
 * @param retryDelay Milliseconds to wait before reloading (no-update path).
 * @returns A promise that never resolves.
 */
const handleRetryableError = (error: Error, retryDelay: number): Promise<never> => {
  console.warn('Erro de carregamento detectado, tentando recarregar...', {
    name: error.name,
    message: error.message,
    stack: error.stack
  });

  if (_updateAvailable) {
    showUpdateToast();
    reloadPageAfterDelay(300);
    return new Promise(() => {});
  }

  clearBrowserCache();
  showUpdateToast();
  reloadPageAfterDelay(retryDelay);

  return new Promise(() => {});
};

/**
 * Wraps a lazy dynamic-import factory so transient chunk/network failures
 * trigger a recovery reload instead of surfacing a broken route.
 *
 * @param importFn The dynamic import to guard (e.g. `() => import('./Page')`).
 * @param options Optional retry configuration (currently `retryDelay`).
 * @returns A new import factory with retry-on-chunk-error behavior.
 */
export function withChunkRetry(
  importFn: () => Promise<any>,
  options: ChunkRetryOptions = {}
) {
  const { retryDelay = 1000 } = options;

  return () => {
    return importFn().catch((error) => {
      if (isRetryableError(error)) {
        return handleRetryableError(error, retryDelay);
      }
      
      throw error;
    });
  };
}
